import { ActionIcon, Paper, Stack, Table, Text, Title, Tooltip } from "@mantine/core";
import { IMinUserDepricated, IProjectDeprecated } from "@mctypes/global.types";
import { IMcBimMongo } from "@mctypes/mcbimMono.types";
import { IconTrash } from "@tabler/icons-react";
import React, { useState } from "react";
import { setShowSuccessMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { ToastMessagesEnums } from "../../../../../mc-shared/sharedConstants";
import { formatShortDate } from "../../selectors";
import { doGetMcBimMongoFileVersions } from "../McBimMongoAPI";
import { useRemoveMcFileDataMutation } from "../McBimRTK";
import { isMcBimAdmin } from "../mcBimSlice";
import { InfoDialog } from "./McBimMongoExtractedFiles";

const FileVersions: React.FC<{ file: IMcBimMongo.FileIdAndName; project: IProjectDeprecated }> = ({
  file,
  project,
}) => {
  const [fileVersions, setFileVersions] = useState<IMcBimMongo.FileStats[]>([]);
  const [nrOfDeletedDocuments, setNrOfDeletedDocuments] = useState<number>(null);

  const user = useAppSelector((state) => state.globalReducer.user);
  const userIsMcBimAdmin = isMcBimAdmin(project.mcbim.admins as IMinUserDepricated[], user?._id);

  const [removeFiles] = useRemoveMcFileDataMutation();

  React.useEffect(() => {
    init();
  }, []);

  const init = async () => {
    const _fileVersions = await doGetMcBimMongoFileVersions(project._id, file);
    setFileVersions(_fileVersions);
  };

  const dispatch = useAppDispatch();
  const removeSpecificFileVersion = async (fileStorageId: string, fileUploadedAt: string, fileName: string) => {
    const shouldDelete = window.confirm(
      `Er du sikker på at du vil slette versjon ${formatShortDate(fileUploadedAt)} av filen ${fileName}?`,
    );
    if (!shouldDelete) {
      return;
    }

    const resp = await removeFiles({ projectId: project._id, fileStorageId, fileUploadedAt }).unwrap();
    dispatch(setShowSuccessMessageThunk(ToastMessagesEnums.UPDATED));
    setFileVersions(fileVersions.filter((f) => f._id.fileStorageId !== fileStorageId));
    setNrOfDeletedDocuments(resp.deletedDocuments);
  };

  return (
    <Paper withBorder radius="md" p="md">
      <InfoDialog deletedDocuments={nrOfDeletedDocuments} toggle={() => setNrOfDeletedDocuments(null)} />
      <Stack gap="sm">
        <div>
          <Title order={4}>Versjoner</Title>
          <Text c="dimmed" size="sm">
            {`Totalt ${fileVersions?.length} versjoner av filen ${file.fileName}`}
          </Text>
        </div>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th>Fil opplastet</Table.Th>
              <Table.Th>Antall BIM-objekter</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {fileVersions.map((version) => (
              <Table.Tr key={`${version._id.fileStorageId}-${version._id.fileUploadedAt}`}>
                <Table.Td w={42}>
                  <Tooltip label="Slett versjon" withArrow>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      disabled={userIsMcBimAdmin === false}
                      onClick={() =>
                        removeSpecificFileVersion(
                          version._id.fileStorageId,
                          version._id.fileUploadedAt?.toString() ?? "",
                          version._id.fileName,
                        )
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Table.Td>
                <Table.Td>{formatShortDate(version._id.fileUploadedAt?.toString() ?? "")}</Table.Td>
                <Table.Td>{version.nrOfBimObjects ?? (version as { count?: number }).count ?? 0}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Paper>
  );
};

export default FileVersions;
